import type { ChangeEvent } from 'react'
import { useCallback } from 'react'

import type { EmojiClickData } from 'emoji-picker-react'
import EmojiPicker, { Theme } from 'emoji-picker-react'
import emojiRegex from 'emoji-regex'
import { Controller, useFormContext } from 'react-hook-form'
import { useQueryClient } from 'react-query'

import EmojiIcon from '@/assets/icons/icon-emoji.svg'
import { Toggle } from '@/components/ui/Toggle.tsx'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover.tsx'
import { TextField } from '@/components/ui/text-field.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { useSettingsContext } from '@/modules/creator-settings/state/SettingsProvider.tsx'
import { SettingsOption } from '@/modules/creator-settings/ui/SettingsOption'
import { debounce } from '@/utils/helpers.ts'
import { useIsDarkTheme } from '@/utils/useTheme.tsx'

import { useDisplaySettingsMutations } from '../hooks/use-display-settings-mutations.tsx'

export const DisplaySettingsEmojis = () => {
  const queryClient = useQueryClient()
  const isDarkMode = useIsDarkTheme()

  const { setSettingsData } = useSettingsContext()
  const { control, watch, setValue } = useFormContext()

  const { toggleEmojisMutation, setEmojisMutation } =
    useDisplaySettingsMutations()

  const toast = useToast()

  const { showEmojis, emojis } = watch()

  const handleEmojiToggle = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      const updateToggle = toggleEmojisMutation.mutateAsync(target.checked)

      updateToggle.then(() => {
        setSettingsData((prevState) => {
          return { ...prevState, emojiTaskbarEnabled: target.checked }
        })
        toast.success('Successfully toggled Emoji Taskbar...')
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toggleEmojisMutation, queryClient, toast],
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sendUpdateToBackend = useCallback(
    debounce((emoji: string) => {
      const updateEmojis = setEmojisMutation.mutateAsync(emoji)

      updateEmojis.then(() => {
        setSettingsData((prevState) => {
          return { ...prevState, emojiTaskbar: emoji }
        })
        toast.success('Successfully updated Emojis')
      })
    }, 500),
    [],
  )
  const handleEmojiChange = useCallback(
    (emojis: string) => {
      const uniqueEmojis = stripDuplicateEmojis(emojis)

      setValue('emojis', uniqueEmojis)
      sendUpdateToBackend(uniqueEmojis)
    },
    [sendUpdateToBackend, setValue],
  )

  const handleEmojiSelection = useCallback(
    ({ emoji }: EmojiClickData) => {
      const newEmojisString = emojis + emoji

      if (stripDuplicateEmojis(newEmojisString) === emojis) {
        return toast.error("You can't add an emoji twice.")
      }

      handleEmojiChange(newEmojisString)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [emojis, handleEmojiChange],
  )

  return (
    <SettingsOption
      title="Show Emojis"
      mobileVertical
      description={
        <div className="flex items-center gap-1">
          <span>Shows an emoji panel above your chat field</span>
        </div>
      }
    >
      <div className="grid grid-flow-col items-center gap-4">
        {showEmojis && (
          <Popover>
            <Controller
              name="emojis"
              control={control}
              render={({ field }) => (
                <TextField
                  name={field.name}
                  value={field.value}
                  onChange={(event) => {
                    field.onChange(event)
                    handleEmojiChange(event.target.value)
                  }}
                  prefix={
                    <>
                      <PopoverTrigger>
                        <div className="flex items-center">
                          <EmojiIcon
                            width={16}
                            height={16}
                            className="text-primary-500 hover:text-primary-400 cursor-pointer"
                          />
                          <span className="text-border pl-1">|</span>
                        </div>
                      </PopoverTrigger>
                      <PopoverContent
                        className="mb-10 w-full border-none bg-transparent p-0 shadow-xl"
                        align="start"
                        side="left"
                        sideOffset={20}
                      >
                        <EmojiPicker
                          onEmojiClick={handleEmojiSelection}
                          theme={isDarkMode ? Theme.DARK : Theme.LIGHT}
                        />
                      </PopoverContent>
                    </>
                  }
                />
              )}
            />
          </Popover>
        )}

        <p className="text-title text-sm font-medium">Activate</p>

        <Controller
          name="showEmojis"
          control={control}
          render={({ field }) => (
            <Toggle
              name={field.name}
              checked={field.value}
              onChange={(isToggled) => {
                field.onChange(isToggled)
                handleEmojiToggle(isToggled)
              }}
            />
          )}
        />
      </div>
    </SettingsOption>
  )
}

function stripDuplicateEmojis(emojiString: string) {
  const regex = emojiRegex()
  const matchedEmojis: string[] = []

  for (const match of emojiString.matchAll(regex)) {
    const emoji = match[0]

    if (matchedEmojis.includes(emoji)) {
      continue
    }

    matchedEmojis.push(emoji)
  }

  return matchedEmojis.join('')
}
